import { defineStore } from "pinia";
import { rrulestr, RRuleSet } from "rrule";
import { DateTime } from "luxon";

import type {
  Calendar,
  CalendarEvent,
  CalendarEventFilterQuery,
  CalendarEventReminder,
  CalendarsConfig,
  CalendarUpdateQuery,
  PatientWithAppointment,
} from "./Models/Domain";
import type { StateStore } from "./Models/State";
import type { CALENDAR_TYPE } from "./Models/Common";

import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";

import fetchCalendarsEventsAH from "./ActionHandlers/fetchCalendarsEvents";
import fetchCalendarsAH from "./ActionHandlers/fetchCalendars";
import fetchAuthLinkAH from "./ActionHandlers/fetchAuthLink";
import updateCalendarAH from "./ActionHandlers/updateCalendar";
import fetchPatientsAH from "./ActionHandlers/fetchPatients";
import createEventAH from "./ActionHandlers/createEvent";
import updateEventAH from "./ActionHandlers/updateEvent";
import removeEventAH from "./ActionHandlers/removeEvent";
import filterByPatientAH from "./ActionHandlers/filterByPatient";
import RemoveFilterByPatientAH from "./ActionHandlers/RemoveFilterByPatient";
import RemoveIntegrationAH from "./ActionHandlers/removeIntegration";
import updateCalendarQueryAH from "./ActionHandlers/updateCalendarQuery";
import fetchConfigActionHandler from "./ActionHandlers/fetchConfig";
import updateConfigActionHandler from "./ActionHandlers/updateConfig";
import createReminderActionHandler from "./ActionHandlers/createCalendarReminder";
import updateCalendarReminderActionHandler from "./ActionHandlers/updateCalendarReminder";
import deleteCalendarReminderActionHandler from "./ActionHandlers/deleteCalendarReminder";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";

const calendarsState: StateStore = {
  calendars: [],
  events: [],
  patients: [],
  query: {},
  isLoadingMore: false,
  config: undefined,
};

export const useCalendarsStore = defineStore(StateNamespace.CALENDARS, {
  state: () => ({ ...calendarsState }),
  getters: {
    patientsMap(): Map<string, PatientWithAppointment> {
      const map = new Map<string, PatientWithAppointment>();

      this.patients.forEach((patient: PatientWithAppointment) => {
        map.set(patient.id, patient);
      });

      return map;
    },

    calendarsMap(): Map<string, Calendar & { isFiltering?: boolean }> {
      const map = new Map<string, Calendar & { isFiltering?: boolean }>();

      this.calendars.forEach((calendar) => {
        map.set(calendar.uuid, calendar);
      });

      return map;
    },

    filteredEvents(): CalendarEvent[] {
      const events = this.events.filter((event) => {
        if (event.isDeleted) return false;
        const calendar = this.calendarsMap.get(event.calendarUuid);

        if (hasValue(calendar)) {
          return !hasValue(calendar.isFiltering) || calendar.isFiltering === true;
        }

        return false;
      });

      const recurrentEvents = events.filter((event) => {
        if (hasValue(event.recurrence) && isValidString(event.remoteId)) {
          if (event.isDeleted) return false;

          let recurrence: string | undefined = undefined;

          try {
            recurrence = (JSON.parse(event.recurrence!.replace(/'/g, '"')) as string[])[0];
          } catch (e) {
            recurrence = event.recurrence!;
          }

          const rule = rrulestr(recurrence!, {
            dtstart: DateTime.fromSeconds(event.startAt).toJSDate(),
          });

          if (hasValue(this.query.fromDate) && hasValue(this.query.toDate)) {
            return rule.between(
              DateTime.fromSeconds(this.query.fromDate).toJSDate(),
              DateTime.fromSeconds(this.query.toDate).toJSDate(),
              true
            );
          }

          return true;
        }
      });
      const recurrentMapEvents = new Map<string, CalendarEvent[]>();
      const nonRecurrentEvents: CalendarEvent[] = [];

      recurrentEvents.forEach((event) => {
        let recurrence: string = "";

        try {
          recurrence = (JSON.parse(event.recurrence!.replace(/'/g, '"')) as string[])[0]!;
        } catch (e) {
          recurrence = event.recurrence!;
        }

        const rule = rrulestr(recurrence);
        const remoteId = event.remoteId!.split("_")[0]!;

        event.recurrence = rule.toString().replace("RRULE:", "");

        if (!recurrentMapEvents.has(remoteId)) {
          recurrentMapEvents.set(remoteId, []);
        }

        recurrentMapEvents.get(remoteId)!.push(event);
      });

      events.forEach((event) => {
        if (hasValue(event.recurrence) && isValidString(event.remoteId)) {
          return;
        }

        if (isValidString(event.remoteId) && new RegExp("[a-z0-9]+_[0-9a-z]+", "gi").test(event.remoteId)) {
          if (event.isDeleted) {
            const id = event.remoteId!.split("_")[0]!;

            const rEvents = recurrentMapEvents.get(id);

            if (hasValue(rEvents)) {
              const extDate = DateTime.fromSeconds(event.startAt);

              recurrentMapEvents.set(
                id,
                rEvents.map((rEvent) => {
                  const rrule = rrulestr(rEvent.recurrence!);
                  const rSet = new RRuleSet();

                  rSet.rrule(rrule);
                  rSet.exdate(extDate.toJSDate());

                  return {
                    ...rEvent,
                    recurrence: rSet.toString(),
                  };
                })
              );
            }

            return;
          }

          const id = event.remoteId.split("_")[0]!;
          const rEvents = recurrentMapEvents.get(id)?.map((rEvent) => {
            const getExDate = (_eventDate: DateTime, exDate: Date): Date => {
              const exDateTime = DateTime.fromJSDate(exDate);

              return exDateTime
                .setZone("UTC")
                .set({
                  hour: 0,
                  minute: 0,
                  second: 0,
                })
                .toJSDate();
            };
            const dtStart = DateTime.fromSeconds(rEvent.startAt);
            const rrule = rrulestr(rEvent.recurrence!, {
              dtstart: dtStart.toJSDate(),
            });
            const startAt = DateTime.fromSeconds(event.startAt)
              .set({ hour: 23, minute: 59, second: 59 })
              .minus({ day: 1 });
            const endAt = startAt.plus({ day: 1 });
            const excluded = rrule.between(startAt.toJSDate(), endAt.toJSDate(), true);

            if (hasValue(excluded) && excluded.length > 0) {
              const rRuleSet = new RRuleSet();

              rRuleSet.rrule(rrule);
              excluded.forEach((ex) => rRuleSet.exdate(getExDate(dtStart, ex)));

              rEvent.recurrence = rRuleSet.toString();
            }

            return rEvent;
          });

          if (hasValue(rEvents)) {
            recurrentMapEvents.set(id, rEvents);
          }
        }

        nonRecurrentEvents.push(event);
      });

      const newEvents: CalendarEvent[] = [];

      recurrentMapEvents.values().forEach((rEvents: CalendarEvent[]) => {
        const getRuleStr = (str: string, startDate: DateTime) => {
          if (str.toUpperCase().includes("DTSTART")) {
            const regExp = new RegExp("DTSTART:[0-9]+T[0-9]+Z", "gi");
            const [match] = str.match(regExp)!;
            const regExpTime = new RegExp("T[0-9]+Z", "gi");
            const [matchTime] = str.match(regExpTime)!;

            return str.replace(match, match.replace(matchTime, `T000000Z`));
          } else {
            return `DTSTART:${startDate.toFormat("yyyyLLdd'T000000Z'")}\n${str}`;
          }
        };

        return rEvents.forEach((e: CalendarEvent) => {
          const rrule = rrulestr(getRuleStr(e.recurrence!, DateTime.fromSeconds(e.startAt).setZone("UTC")));
          const startAt = hasValue(this.query.fromDate) ? this.query.fromDate : e.startAt;
          const endAt = hasValue(this.query.toDate) ? this.query.toDate : e.endAt;

          const dates = rrule.between(
            DateTime.fromSeconds(startAt).toJSDate(),
            DateTime.fromSeconds(endAt).toJSDate(),
            false
          );

          return dates.forEach((isoDate: Date, index: number) => {
            const startDay = DateTime.fromJSDate(isoDate).toFormat("dd/MM/yyyy");
            const startHour = DateTime.fromSeconds(e.startAt, { zone: e.timeZone }).toFormat("HH:mm");
            const endHour = DateTime.fromSeconds(e.endAt, { zone: e.timeZone }).toFormat("HH:mm");
            const startAt = DateTime.fromFormat(`${startDay} ${startHour}`, "dd/MM/yyyy HH:mm").toSeconds();
            const endAt = DateTime.fromFormat(`${startDay} ${endHour}`, "dd/MM/yyyy HH:mm").toSeconds();

            newEvents.push({
              ...e,
              uuid: `${e.uuid}_${index}`,
              startAt,
              endAt,
            });
          });
        });
      });

      return [...newEvents, ...nonRecurrentEvents];
    },
  },
  actions: {
    toogleFilterCalendar(uuid: string) {
      const index = this.calendars.findIndex((c) => c.uuid === uuid);

      if (index !== -1) {
        this.calendars[index]!.isFiltering =
          !hasValue(this.calendars[index]!.isFiltering) || this.calendars[index]!.isFiltering === true
            ? false
            : true;
      }
    },

    fetchCalendars(): Promise<Calendar[]> {
      return fetchCalendarsAH();
    },

    fetchCalendarEvents(query: CalendarEventFilterQuery = {}): Promise<CalendarEvent[]> {
      return fetchCalendarsEventsAH(query);
    },

    fetchAuthLink(provider: CALENDAR_TYPE): Promise<string | undefined> {
      return fetchAuthLinkAH(provider);
    },

    updateCalendar(uuid: string, query: CalendarUpdateQuery): Promise<Calendar | undefined> {
      return updateCalendarAH(uuid, query);
    },

    fetchPatients(): Promise<PatientWithAppointment[]> {
      return fetchPatientsAH();
    },

    createEvent(
      calendarUuid: string,
      event: Omit<CalendarEvent, "uuid" | "calendarUuid" | "isEditable">
    ): Promise<CalendarEvent | undefined> {
      return createEventAH(calendarUuid, event);
    },

    updateEvent(event: Omit<CalendarEvent, "isEditable">): Promise<CalendarEvent | undefined> {
      return updateEventAH(event);
    },

    removeEvent(event: Omit<CalendarEvent, "isEditable">): Promise<CalendarEvent | undefined> {
      return removeEventAH(event);
    },

    enableFilterByPatient(patientUuid: string) {
      filterByPatientAH(patientUuid);
    },

    removeFilterByPatient(calendarUuid: string) {
      RemoveFilterByPatientAH(calendarUuid);
    },

    removeIntegration(provider: CALENDAR_TYPE, integrationId: string): Promise<true | undefined> {
      return RemoveIntegrationAH(provider, integrationId);
    },

    updateCalendarQuery(query: CalendarEventFilterQuery): Promise<CalendarEvent[]> {
      return updateCalendarQueryAH(query);
    },

    fetchConfig(): Promise<CalendarsConfig> {
      return fetchConfigActionHandler();
    },

    updateCancelationPolicyTime(time: number): Promise<CalendarsConfig> {
      this.config!.cancellationPolicyTime = time;

      return updateConfigActionHandler();
    },

    createEventReminder(
      reminder: Omit<CalendarEventReminder, "uuid">
    ): Promise<CalendarEventReminder | undefined> {
      return createReminderActionHandler(reminder);
    },

    updateEventReminder(reminder: CalendarEventReminder): Promise<CalendarEventReminder | undefined> {
      return updateCalendarReminderActionHandler(reminder);
    },

    deleteEventReminder(reminder: CalendarEventReminder): Promise<boolean> {
      return deleteCalendarReminderActionHandler(reminder);
    },
  },
});

export type CalendarsStore = ReturnType<typeof useCalendarsStore>;

export default useCalendarsStore;
